import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { shareApi } from '../services/api';
import { importAESKey, decryptBabyProfile, decryptEventData } from '../crypto';
import { format } from 'date-fns';

interface ShareData {
  babyName: string;
  encryptedKey: string;
  encryptedProfile: string;
  events: Array<{
    id: string;
    type: string;
    timestamp: string;
    encryptedData: string;
  }>;
  expiresAt: string;
  sharedBy: string;
  eventTypes: string[] | null;
  dateFrom: string | null;
  dateTo: string | null;
}

interface DecryptedEvent {
  id: string;
  type: string;
  timestamp: Date;
  data: any;
}

const EVENT_TYPE_INFO: Record<string, { icon: string; label: string; color: string }> = {
  FEED: { icon: '🍼', label: 'Feed', color: 'bg-blue-100 dark:bg-blue-900' },
  NAPPY: { icon: '🧷', label: 'Nappy', color: 'bg-yellow-100 dark:bg-yellow-900' },
  SPIT_UP: { icon: '💧', label: 'Spit Up', color: 'bg-orange-100 dark:bg-orange-900' },
  MEDICATION: { icon: '💊', label: 'Medication', color: 'bg-red-100 dark:bg-red-900' },
  SLEEP: { icon: '😴', label: 'Sleep', color: 'bg-purple-100 dark:bg-purple-900' },
  WEIGHT: { icon: '⚖️', label: 'Weight', color: 'bg-green-100 dark:bg-green-900' },
  TEMPERATURE: { icon: '🌡️', label: 'Temperature', color: 'bg-pink-100 dark:bg-pink-900' },
  TUMMY_TIME: { icon: '🏋️', label: 'Tummy Time', color: 'bg-teal-100 dark:bg-teal-900' },
  PUMP: { icon: '🥛', label: 'Pump', color: 'bg-indigo-100 dark:bg-indigo-900' },
  BOTTLE: { icon: '🍶', label: 'Bottle', color: 'bg-cyan-100 dark:bg-cyan-900' },
  JOURNAL: { icon: '📝', label: 'Journal', color: 'bg-gray-100 dark:bg-gray-800' },
};

export default function SharedView() {
  const { token } = useParams();
  const [decryptedProfile, setDecryptedProfile] = useState<any>(null);
  const [decryptedEvents, setDecryptedEvents] = useState<DecryptedEvent[]>([]);
  const [decryptError, setDecryptError] = useState<string | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);

  // Fetch shared data
  const { data: shareData, isLoading, error } = useQuery({
    queryKey: ['share', token],
    queryFn: async () => {
      if (!token) throw new Error('No share token');
      const response = await shareApi.access(token);
      return response.data as ShareData;
    },
    enabled: !!token,
    retry: false,
  });

  // Decrypt the shared data when it loads
  useEffect(() => {
    async function decryptData() {
      if (!shareData) return;

      setIsDecrypting(true);
      setDecryptError(null);

      try {
        // Import the AES key from the share
        const aesKey = await importAESKey(shareData.encryptedKey);

        // Decrypt the profile
        if (shareData.encryptedProfile) {
          const profile = await decryptBabyProfile(shareData.encryptedProfile, aesKey);
          setDecryptedProfile(profile);
        }

        // Decrypt events
        const events: DecryptedEvent[] = [];
        for (const event of shareData.events || []) {
          try {
            const data = await decryptEventData(event.encryptedData, aesKey);
            events.push({
              id: event.id,
              type: event.type,
              timestamp: new Date(event.timestamp),
              data,
            });
          } catch (e) {
            console.error('Failed to decrypt event:', e);
          }
        }

        // Sort by timestamp descending
        events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        setDecryptedEvents(events);
      } catch (err) {
        console.error('Failed to decrypt shared data:', err);
        setDecryptError('Failed to decrypt shared data. The link may be invalid.');
      } finally {
        setIsDecrypting(false);
      }
    }

    decryptData();
  }, [shareData]);

  if (isLoading || isDecrypting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {isLoading ? 'Loading shared data...' : 'Decrypting data...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">❌</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Invalid Share Link
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This share link is invalid, has expired, or has been revoked.
          </p>
        </div>
      </div>
    );
  }

  if (decryptError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">🔒</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Decryption Error
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {decryptError}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-4xl">👶</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {decryptedProfile?.name || shareData.babyName}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Shared by {shareData.sharedBy}
          </p>
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm">
            <span>🔒</span>
            Read-only access
          </div>
        </div>

        {/* Profile Info */}
        {decryptedProfile && (
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Profile Information
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Date of Birth</span>
                <p className="text-gray-900 dark:text-white">
                  {format(new Date(decryptedProfile.dateOfBirth), 'MMMM d, yyyy')}
                </p>
              </div>
              {decryptedProfile.gender && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Gender</span>
                  <p className="text-gray-900 dark:text-white capitalize">
                    {decryptedProfile.gender}
                  </p>
                </div>
              )}
              {decryptedProfile.bloodType && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Blood Type</span>
                  <p className="text-gray-900 dark:text-white">{decryptedProfile.bloodType}</p>
                </div>
              )}
              {decryptedProfile.allergies?.length > 0 && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Allergies</span>
                  <p className="text-red-600 dark:text-red-400 font-medium">
                    {decryptedProfile.allergies.join(', ')}
                  </p>
                </div>
              )}
              {decryptedProfile.medications?.length > 0 && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Medications</span>
                  <p className="text-gray-900 dark:text-white">
                    {decryptedProfile.medications.join(', ')}
                  </p>
                </div>
              )}
              {decryptedProfile.pediatrician && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Pediatrician</span>
                  <p className="text-gray-900 dark:text-white">
                    {decryptedProfile.pediatrician.name}
                    {decryptedProfile.pediatrician.phone && ` • ${decryptedProfile.pediatrician.phone}`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Events */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Events
            {shareData.eventTypes && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                (Filtered: {shareData.eventTypes.join(', ')})
              </span>
            )}
          </h2>

          {decryptedEvents.length > 0 ? (
            <div className="space-y-3">
              {decryptedEvents.map((event) => {
                const info = EVENT_TYPE_INFO[event.type] || { icon: '📋', label: event.type, color: 'bg-gray-100 dark:bg-gray-800' };

                return (
                  <div key={event.id} className={`p-4 rounded-lg ${info.color}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{info.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {info.label}
                          </p>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {format(event.timestamp, 'MMM d, h:mm a')}
                          </span>
                        </div>
                        {/* Display event-specific data */}
                        <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {event.type === 'FEED' && event.data.feedType && (
                            <span>
                              {event.data.feedType === 'breast' && `Breast • ${event.data.side || 'both'} side`}
                              {(event.data.feedType === 'formula' || event.data.feedType === 'bottle') && `Formula • ${event.data.amount || '?'} ml`}
                              {event.data.duration && ` • ${event.data.duration} min`}
                            </span>
                          )}
                          {event.type === 'NAPPY' && (
                            <span>
                              {event.data.wet && 'Wet'}
                              {event.data.wet && event.data.dirty && ' + '}
                              {event.data.dirty && 'Dirty'}
                            </span>
                          )}
                          {event.type === 'SLEEP' && event.data.duration && (
                            <span>{event.data.duration} minutes</span>
                          )}
                          {event.type === 'MEDICATION' && event.data.medicationName && (
                            <span>{event.data.medicationName} {event.data.dose && `• ${event.data.dose}`}</span>
                          )}
                          {event.type === 'WEIGHT' && event.data.weight && (
                            <span>{event.data.weight} {event.data.unit || 'kg'}</span>
                          )}
                          {event.type === 'TEMPERATURE' && event.data.temperature && (
                            <span>{event.data.temperature}° {event.data.tempUnit || 'C'}</span>
                          )}
                          {event.data.notes && (
                            <p className="text-gray-500 dark:text-gray-400 mt-1 italic">
                              "{event.data.notes}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No events to display.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            This shared view expires on {format(new Date(shareData.expiresAt), 'MMMM d, yyyy')}
          </p>
          <p className="mt-2">
            🔒 Data is end-to-end encrypted • Powered by myTotTot
          </p>
        </div>
      </div>
    </div>
  );
}
